import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Crown, Star, Lock } from "lucide-react";
import { useEffect, useState } from "react";
const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a]">
      {/* Animated Background Gradient Overlay */}
      <div className="absolute inset-0 animate-gradient-shift opacity-15">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent" />
      </div>

      {/* Radial Spotlight Effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-radial from-primary/30 via-transparent to-transparent blur-3xl" />

      {/* Floating Particles Effect */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary rounded-full animate-float" />
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-accent rounded-full animate-float delay-300" />
        <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-secondary rounded-full animate-float delay-500" />
        <div className="absolute bottom-1/3 right-1/3 w-3 h-3 bg-tertiary rounded-full animate-float delay-200" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 lg:px-16 py-20 md:py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center space-y-6 md:space-y-8">
          
          {/* Top Badge */}
          <div className={`inline-flex items-center gap-2 ${isVisible ? 'animate-fade-in-up opacity-0' : 'opacity-0'}`}>
            <div className="px-4 py-2 rounded-full bg-black/60 backdrop-blur-sm border border-primary/30 text-white text-xs md:text-sm font-medium animate-pulse-glow">
              <span className="flex items-center gap-2">
                <Lock className="w-3 h-3 md:w-4 md:h-4" />
                <span>Invitation Only | &lt;5% Acceptance Rate</span>
              </span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="space-y-2">
            <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight ${isVisible ? 'animate-fade-in-up delay-100 opacity-0' : 'opacity-0'}`}>
              <span className="block text-white drop-shadow-2xl">
                Not Everyone Gets In.
              </span>
            </h1>
            <h1 className={`text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-extrabold leading-tight ${isVisible ? 'animate-fade-in-up delay-200 opacity-0' : 'opacity-0'}`}>
              <span className="block bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-shimmer drop-shadow-2xl">
                But Those Who Do Keep Everything.
              </span>
            </h1>
          </div>

          {/* Subheadline */}
          <p className={`text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed ${isVisible ? 'animate-fade-in-up delay-300 opacity-0' : 'opacity-0'}`}>
            An ultra-exclusive TikTok Live network for rapid-growth and celebrity-level creators. Zero fees. No contracts. Elite 24/7 support. If you're accepted, you'll scale faster, earn more, and join the top 1% of TikTok Live talent.
          </p>

          {/* Scarcity Statement */}
          <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-black/40 backdrop-blur-sm border border-accent/30 ${isVisible ? 'animate-fade-in-up delay-400 opacity-0' : 'opacity-0'}`}>
            <Lock className="w-5 h-5 text-accent" />
            <p className="text-sm md:text-base lg:text-lg font-bold text-accent">
              Limited spots remaining. Over 1,200 creators applied. Less than 300 accepted. Ever.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center pt-6 ${isVisible ? 'animate-fade-in-up delay-500 opacity-0' : 'opacity-0'}`}>
            <Link to="/apply" className="w-full sm:w-auto">
              <Button variant="premium" size="lg" className="group w-full sm:w-auto text-base md:text-lg px-8 md:px-12 py-6 md:py-7 font-bold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(217,70,239,0.6)] bg-gradient-to-r from-primary to-accent">
                See If You Qualify
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/program" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto text-base md:text-lg px-8 md:px-10 py-6 md:py-7 font-semibold border-2 border-primary/50 hover:bg-gradient-to-r hover:from-primary/20 hover:to-accent/20 hover:border-accent/70 transition-all duration-300 backdrop-blur-sm">
                See the Requirements
              </Button>
            </Link>
          </div>

          {/* Trust Indicators / Stats Bar */}
          <div className={`pt-12 md:pt-20 ${isVisible ? 'animate-fade-in-up delay-600 opacity-0' : 'opacity-0'}`}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
              
              {/* Stat 1: 0% Fees */}
              <div className="p-6 rounded-2xl bg-card/40 backdrop-blur-sm border border-border/50 hover:scale-105 transition-transform duration-300">
                <div className="flex flex-col items-center space-y-3">
                  <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-accent" />
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">
                    0%
                  </div>
                  <div className="text-sm md:text-base font-semibold text-gray-300">
                    Platform Fees
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">
                    Keep 100% forever
                  </div>
                </div>
              </div>

              {/* Stat 2: 24/7 Support */}
              <div className="p-6 rounded-2xl bg-card/40 backdrop-blur-sm border border-border/50 hover:scale-105 transition-transform duration-300">
                <div className="flex flex-col items-center space-y-3">
                  <Crown className="w-8 h-8 md:w-10 md:h-10 text-accent" />
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-accent to-tertiary bg-clip-text text-transparent">
                    24/7
                  </div>
                  <div className="text-sm md:text-base font-semibold text-gray-300">
                    Elite Support
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">
                    VIP access always
                  </div>
                </div>
              </div>

              {/* Stat 3: 12+ Years */}
              <div className="p-6 rounded-2xl bg-card/40 backdrop-blur-sm border border-border/50 hover:scale-105 transition-transform duration-300">
                <div className="flex flex-col items-center space-y-3">
                  <Star className="w-8 h-8 md:w-10 md:h-10 text-primary" />
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent">
                    12+
                  </div>
                  <div className="text-sm md:text-base font-semibold text-gray-300">
                    Years Experience
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">
                    A-list expertise
                  </div>
                </div>
              </div>

              {/* Stat 4: <5% Acceptance */}
              <div className="p-6 rounded-2xl bg-card/40 backdrop-blur-sm border border-border/50 hover:scale-105 transition-transform duration-300">
                <div className="flex flex-col items-center space-y-3">
                  <Lock className="w-8 h-8 md:w-10 md:h-10 text-yellow-500" />
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-yellow-500 to-orange-500 bg-clip-text text-transparent">
                    &lt;5%
                  </div>
                  <div className="text-sm md:text-base font-semibold text-gray-300">
                    Acceptance Rate
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">
                    Ultra-selective
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Social Proof Line */}
          <p className={`text-sm md:text-base text-gray-400 italic pt-6 ${isVisible ? 'animate-fade-in-up delay-700 opacity-0' : 'opacity-0'}`}>
            Where elite creators go to dominate TikTok Live
          </p>

        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>;
};
export default Hero;